import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploaderService } from '../../core/services/file-uploader.service';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToasterService } from '../toaster.service'; // ✅ Import ToasterService

export interface FileUploadResult {
  file: File;
  url?: string;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true
    }
  ]
})
export class FileUploadComponent implements ControlValueAccessor, OnDestroy {
  @Input() label: string = 'Upload File';
  @Input() required: boolean = false;
  @Input() tooltipText: string = '';
  @Input() acceptedTypes: string = '.pdf';
  @Input() maxSizeMB: number = 20;
  @Input() errorMessage: string = 'Please upload a valid PDF file';
  @Input() filePath: string = '';
  @Input() type?: any; 
  @Input() extraData?: {
  name?: string;
  idNumber?: string;
};
  @Output() uploadComplete = new EventEmitter<FileUploadResult>();
  @Output() fileRemoved = new EventEmitter<void>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  existingFileUrl: string = '';
  localPdfUrl: string = '';
  presignedUrl: string = '';
  fileName: string = '';
  fileSize: string = '';
  showError: boolean = false;
  isUploading: boolean = false;
  isFetchingPresignedUrl: boolean = false;
  uploadProgress: number = 0;
  uploadedFileUrl: string = '';
  showPopup: boolean = false;
  Math = Math;
  disabled = false;

  private uploadSubscription?: Subscription;

  constructor(
    private fileUploaderService: FileUploaderService,
    private sanitizer: DomSanitizer,
    private toaster: ToasterService // ✅ Inject ToasterService
  ) { }

  ngOnDestroy(): void {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    if (this.localPdfUrl) {
      URL.revokeObjectURL(this.localPdfUrl);
    }
  }

  private onChange = (value: File | string | null) => { };
  private onTouched = () => { };

  writeValue(value: File | string | null): void {
    if (value !== (this.selectedFile || this.existingFileUrl)) {
      if (!value) {
        this.clearFileUI();
      } else if (typeof value === 'string') {
        this.selectedFile = null;
        this.existingFileUrl = value;
        this.uploadedFileUrl = value;
        this.fileName = this.extractFileName(value);
        this.presignedUrl = '';
        if (!this.fileSize || this.fileSize === 'N/A') {
          this.fileSize = '  ';
        }
        this.showError = false;
      } else {
        this.existingFileUrl = '';
        this.presignedUrl = '';
        this.selectedFile = value as File;
        this.fileName = value.name;
        const fileSizeMB = value.size / (1024 * 1024);
        this.fileSize = fileSizeMB.toFixed(2);
        this.localPdfUrl = URL.createObjectURL(value);
        this.showError = false;
      }
      this.onChange(value);
    }
  }

  private extractFileName(url: string): string {
    const pathParts = url.split('/');
    let fileName = pathParts[pathParts.length - 1];
    if (!fileName || fileName.includes(':')) {
      fileName = 'Existing file';
    }
    return fileName;
  }

  registerOnChange(fn: (value: File | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Handle disabled state if needed
    this.disabled = isDisabled;
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];

    if (file) {
      // ✅ Validate file type
      if (!this.isValidFileType(file)) {
        this.showError = true;
        this.errorMessage = `نوع ملف غير صالح. يرجى اختيار ملف ${this.acceptedTypes}`;
        this.toaster.error(this.errorMessage); // ✅ Show toaster
        this.clearFileInput();
        return;
      }

      // ✅ Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > this.maxSizeMB) {
        this.showError = true;
        this.errorMessage = `حجم الملف يتجاوز ${this.maxSizeMB}MB. يرجى اختيار ملف أصغر`;
        this.toaster.error(this.errorMessage); // ✅ Show toaster
        this.clearFileInput();
        return;
      }

      // ✅ Validate filePath
      if (!this.filePath) {
        this.showError = true;
        this.errorMessage = 'مسار الملف مطلوب للرفع';
        this.toaster.error(this.errorMessage); // ✅ Show toaster
        this.clearFileInput();
        return;
      }

      // ✅ Set file details
      this.existingFileUrl = '';
      this.presignedUrl = '';
      this.selectedFile = file;
      this.fileName = file.name;
      this.fileSize = fileSizeMB.toFixed(2);
      this.localPdfUrl = URL.createObjectURL(file);
      this.showError = false;
      this.onChange(file);
      this.onTouched();

      // ✅ AUTO-UPLOAD immediately after selection
      this.uploadFile();
    } else {
      this.removeFile();
    }
  }

  onChooseFile(): void {
    if (this.disabled) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  private clearFileUI(): void {
    if (this.localPdfUrl) {
      URL.revokeObjectURL(this.localPdfUrl);
      this.localPdfUrl = '';
    }

    this.selectedFile = null;
    this.existingFileUrl = '';
    this.uploadedFileUrl = '';
    this.presignedUrl = '';
    this.fileName = '';
    this.fileSize = '';
    this.showError = false;
    this.isUploading = false;
    this.uploadProgress = 0;

    this.clearFileInput();
  }

  removeFile(): void {
    if (this.disabled) {
      return;
    }
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = undefined;
    }

    // ✅ Delete from server if file exists
    if (this.existingFileUrl || this.uploadedFileUrl) {
      const cleanPath = (this.uploadedFileUrl || this.existingFileUrl).replace("Minio-FILE:", "");
      console.log('🗑️ Deleting file from server:', cleanPath);
      this.fileUploaderService.deleteFile(cleanPath).subscribe({
        next: () => console.log('✅ File deleted from server'),
        error: (err) => console.error('❌ Error deleting file:', err)
      });
    }

    this.clearFileUI();
    this.onChange(null);
    this.onTouched();
    this.fileRemoved.emit();
  }

  private clearFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  // uploadFile(): void {
  //   if (!this.selectedFile || !this.filePath) {
  //     return;
  //   }

  //   this.isUploading = true;
  //   this.uploadProgress = 0;
  //   this.showError = false;

  //   if (this.uploadSubscription) {
  //     this.uploadSubscription.unsubscribe();
  //   }

  //   this.uploadSubscription = this.fileUploaderService
  //     .uploadFile(this.filePath, this.selectedFile)
  //     .subscribe({
  //       next: (progress) => {
  //         this.uploadProgress = progress.progress;

  //         if (progress.state === 'DONE') {
  //           this.isUploading = false;
  //           this.uploadProgress = 100;
  //           this.uploadedFileUrl = progress.response?.data[0];

  //           // ✅ Show success toaster
  //           // this.toaster.success('تم رفع الملف بنجاح');

  //           this.uploadComplete.emit({
  //             file: this.selectedFile!,
  //             url: progress.response?.data[0]
  //           });

  //           this.uploadSubscription = undefined;
  //         }
  //       },
  //       error: (err) => {
  //         this.isUploading = false;
  //         this.uploadProgress = 0;
  //         this.showError = true;
  //         this.errorMessage = 'حدث خطأ أثناء رفع الملف';
          
  //         // ✅ Show error toaster
  //         // this.toaster.error('حدث خطأ');
          
  //         console.error('Upload error:', err);
  //         this.uploadSubscription = undefined;
  //       }
  //     });
  // }
  uploadFile(): void {
  if (!this.selectedFile || !this.filePath) {
    return;
  }

  // ✅ Rename file only if type === IdDoc
  let fileToUpload = this.selectedFile;

  if (this.type === 'IdDoc') {
    this.fileName = this.renameIdDocFile(this.selectedFile);
  }

  this.isUploading = true;
  this.uploadProgress = 0;
  this.showError = false;

  if (this.uploadSubscription) {
    this.uploadSubscription.unsubscribe();
  }

  this.uploadSubscription = this.fileUploaderService
    .uploadFile(this.filePath, fileToUpload, {} ,this.fileName)
    .subscribe({
      next: (progress) => {
        this.uploadProgress = progress.progress;

        if (progress.state === 'DONE') {
          this.isUploading = false;
          this.uploadProgress = 100;
          this.uploadedFileUrl = progress.response?.data;

          this.uploadComplete.emit({
            file: fileToUpload,
            url: progress.response?.data
          });

          this.uploadSubscription = undefined;
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.showError = true;
        this.errorMessage = 'حدث خطأ أثناء رفع الملف';
        this.toaster.error(this.errorMessage);

        this.clearFileUI(); 
        this.onChange(null);
        console.error('Upload error:', err);
        this.uploadSubscription = undefined;
      }
    });
}

private renameIdDocFile(file: File): string {
  if (!this.extraData?.name || !this.extraData?.idNumber) {
    return file.name;
  }

  const safeName = this.extraData.name?.length > 100 ? this.extraData.name?.substring(0,100) : this.extraData.name
    .trim()
    .replace(/\s+/g, '_');

  const extension = file.name.split('.').pop();

  const newFileName =
    `${safeName}_${this.extraData.idNumber}`;

  return newFileName;
}


  cancelUpload(): void {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = undefined;
    }

    this.isUploading = false;
    this.uploadProgress = 0;
  }

  private isValidFileType(file: File): boolean {
    const acceptedTypesArray = this.acceptedTypes.split(',').map(type => type.trim());

    for (const acceptedType of acceptedTypesArray) {
      if (acceptedType === '.pdf' && file.type === 'application/pdf') {
        return true;
      }
    }

    return false;
  }

  getAcceptedTypesDescription(): string {
    const types = this.acceptedTypes.split(',').map(type => type.trim().toUpperCase());
    return types.join(', ');
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  get isExistingFile(): boolean {
    return !!this.existingFileUrl && !this.selectedFile;
  }

  get currentValue(): File | string | null {
    return this.selectedFile || this.existingFileUrl || null;
  }

  get pdfUrl(): string {
    if (this.presignedUrl) {
      const cleanBase64 = this.presignedUrl.includes('base64,')
      ? this.presignedUrl.split('base64,')[1]
      : this.presignedUrl;

    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    }
    if (this.localPdfUrl) {
      return this.localPdfUrl;
    }
    return '';
  }

  get safePdfUrl(): SafeResourceUrl {
    if (!this.pdfUrl) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfUrl);
  }

  openPdfPopup(): void {
    if (this.localPdfUrl) {
      this.showPopup = true;
      return;
    }

    if (this.presignedUrl) {
      this.showPopup = true;
      return;
    }

    const fileUrl = this.uploadedFileUrl || this.existingFileUrl;
    if (fileUrl && fileUrl.startsWith('Minio-FILE:')) {
      const cleanPath = fileUrl.replace('Minio-FILE:', '');
      this.isFetchingPresignedUrl = true;

      this.fileUploaderService.getFileData(cleanPath).subscribe({
        next: (response) => {
          this.isFetchingPresignedUrl = false;
          if (response.success && response.data?.presignedUrl) {
            this.presignedUrl = response.data.presignedUrl;
            if (response.data.fileSize) {
              this.fileSize = this.formatFileSize(response.data.fileSize);
            }
            this.showPopup = true;
            console.log('✅ Presigned URL fetched:', this.presignedUrl);
          } else {
            this.showError = true;
            this.errorMessage = 'فشل تحميل معاينة الملف';
            this.toaster.error('حدث خطأ'); // ✅ Show toaster
            console.error('❌ No presigned URL in response:', response);
          }
        },
        error: (err) => {
          this.isFetchingPresignedUrl = false;
          this.showError = true;
          this.errorMessage = 'فشل تحميل معاينة الملف';
          this.toaster.error('حدث خطأ'); // ✅ Show toaster
          console.error('❌ Error fetching presigned URL:', err);
        }
      });
    } else {
      console.error('❌ No valid file URL to preview');
    }
  }

  closePdfPopup(): void {
    this.showPopup = false;
  }
}